import { apiClient } from '@/config/api';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import { Project, HydraCollection, PaginatedResponse } from '@/types';

export interface CreateProjectData {
  title: string;
  code?: string;
  adresse?: string;
  city?: string;
}

export interface UpdateProjectData {
  title?: string;
  code?: string;
  adresse?: string;
  city?: string;
}

export interface GetProjectsParams {
  page?: number;
  itemsPerPage?: number;
  search?: string;
}

export const projectService = {
  // Get all projects with pagination and search
  async getProjects(params?: GetProjectsParams): Promise<PaginatedResponse<Project>> {
    const page = params?.page || 1;
    const itemsPerPage = params?.itemsPerPage || 20;
    const search = params?.search;

    const requestParams: Record<string, unknown> = {
      'groups[]': ['project:read'],
      page: page,
      itemsPerPage: itemsPerPage,
    };

    // Add search parameter if provided
    if (search && search.trim() !== '') {
      requestParams['title'] = search;
    }

    const response = await apiClient.get<HydraCollection<Project>>('/projects', {
      params: requestParams,
    });

    // Handle JSON-LD format from API Platform
    const data = response.data;
    const projects = data['hydra:member'] || data['member'] || [];
    const totalItems = data['hydra:totalItems'] || data['totalItems'] || 0;
    const view = data['hydra:view'] || data['view'];

    // Calculate pagination info
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const hasNextPage = !!view?.['hydra:next'];
    const hasPreviousPage = !!view?.['hydra:previous'];

    return {
      data: projects,
      totalItems,
      currentPage: page,
      itemsPerPage,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    };
  },

  // Get single project
  async getProject(id: number): Promise<Project> {
    const response = await apiClient.get(`/projects/${id}`);
    return response.data;
  },

  // Get minimal map page info for a project
  async getProjectMapInfo(id: number): Promise<Pick<Project, 'id' | 'title' | 'city' | 'logo' | 'mapImage' | 'createdAt'>> {
    const response = await apiClient.get(`/projects/${id}/map-info`);
    return response.data;
  },

  // Get current user's role for a project (project-scoped role)
  async getMyProjectRole(id: number): Promise<{ projectId: number; role: 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER'; isRoot?: boolean }> {
    const response = await apiClient.get(`/projects/${id}/my-role`);
    return response.data;
  },

  // Create project
  async createProject(data: CreateProjectData): Promise<Project> {
    const response = await apiClient.post('/projects', data);
    return response.data;
  },

  // Update project
  async updateProject(id: number, data: UpdateProjectData): Promise<Project> {
    const response = await apiClient.put(`/projects/${id}`, data);
    return response.data;
  },

  // Delete project
  async deleteProject(id: number): Promise<void> {
    await apiClient.delete(`/projects/${id}`);
  },

  // Deep delete project (admin only) - DOES NOT delete users
  async deepDeleteProject(id: number, confirmTitle: string): Promise<{
    projectId: number;
    projectTitle: string;
    deletedAt: string;
  }> {
    const response = await apiClient.post(`/admin/projects/${id}/deep-delete`, { confirmTitle });
    return response.data;
  },

  // Upload logo
  async uploadLogo(id: number, file: File): Promise<{ logo: string }> {
    const formData = new FormData();
    formData.append('logo', file);

    // Don't set Content-Type header - let axios set it automatically with boundary
    const response = await apiClient.post(`/projects/${id}/logo`, formData);
    return response.data;
  },

  // Delete logo
  async deleteLogo(id: number): Promise<void> {
    await apiClient.delete(`/projects/${id}/logo`);
  },

  // Upload map image (used in user map page)
  async uploadImage(id: number, file: File): Promise<{ mapImage: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await apiClient.post(`/projects/${id}/image`, formData);
    return response.data;
  },

  // Delete map image
  async deleteImage(id: number): Promise<void> {
    await apiClient.delete(`/projects/${id}/image`);
  },

  // Get boxes statistics for a project
  async getBoxesStats(projectId: number, page?: string): Promise<{ total: number; online: number; offline: number; onlinePercentage: number }> {
    const params: Record<string, any> = {};
    if (page) {
      params.page = page;
    }
    const response = await apiClient.get(`/projects/${projectId}/boxes/stats`, { params });
    return response.data;
  },

  // Get global boxes statistics
  async getGlobalBoxesStats(): Promise<{ total: number; online: number; offline: number }> {
    const response = await apiClient.get<{ total: number; online: number; offline: number }>(
      '/boxes/stats'
    );
    return response.data;
  },

  // Import equipments using fetch with streaming
  async importEquipmentsStream(
    projectId: number,
    apiUrl: string | undefined,
    boxNamePrefix: string | undefined,
    onMessage: (data: any) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const baseUrl = API_BASE_URL;
    
    const formData = new FormData();
    // apiUrl is optional, can be added later if needed
    if (apiUrl) {
      formData.append('apiUrl', apiUrl);
    }
    if (boxNamePrefix && boxNamePrefix.trim() !== '') {
      formData.append('boxNamePrefix', boxNamePrefix.trim());
    }

    const response = await fetch(`${baseUrl}/projects/${projectId}/import-equipments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('No response body');
    }

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            onMessage(data);
          } catch (e) {
            // Ignore JSON parse errors
          }
        }
      }
    }
  },
};
